import { renderDateEl } from '../../ui/Time/TimePortalUtil';
import { create, nextRepaint } from '../../utils/dom';
import Selection from '../selection/Selection';
import Icon from '../icon';
import {
    CSS_CLASS_BLOCK_NAME,
    SYMBOL_SECTION_CSS_CLASS_BLOCK_NAME,
    DETAILS_SECTION_CSS_CLASS_BLOCK_NAME,
    SELECTION_CSS_CLASS_BLOCK_NAME,
    STATUS_CSS_CLASS_BLOCK_NAME,
    STATUS_INPLAY_CSS_CLASS_MODIFIER,
    SELECTION_NAME_CSS_CLASS_BLOCK_NAME,
    MARKET_NAME_CSS_CLASS_BLOCK_NAME,
    EVENT_NAME_CSS_CLASS_BLOCK_NAME,
    EXTRA_INFO_CSS_CLASS_BLOCK_NAME,
    JOCKEY_CSS_CLASS_BLOCK_NAME,
    TRAINER_CSS_CLASS_BLOCK_NAME,
    DETAILS_LABEL_CSS_CLASS_BLOCK_NAME,
    DETAILS_VALUE_CSS_CLASS_BLOCK_NAME,
    I18N_JOCKEY_LABEL_TEXT,
    I18N_TRAINER_LABEL_TEXT,
    I18N_INPLAY_STATUS_TEXT,
    STATUS_LABEL_CSS_CLASS_BLOCK_NAME,
    TEXT_CSS_CLASS_BLOCK_NAME,
} from './constants';
import {
    HORSE_RACING_SLUG,
    GREYHOUND_RACING_SLUG,
} from '~shared/utils/constants';
import './sport-selection.scss';
import { SIZE_24x24, CLASS_BLOCK_NAME } from '../icon/constants';
import {
    startListeningForInplayEventUpdates,
    onInplaySelectionUpdate,
    onInplayEventOverById,
} from '../../services/inplay';
import {
    startListeningForPreplayEventUpdates,
    onPreplaySelectionUpdate,
    onPreplayEventStartById,
    onPreplayEventOffById,
    stopListeningForPreplayEventUpdates,
} from '../../services/preplay';
import { renderSwitchableOdds } from '~spa/Components/SwitchableOdds/SwitchableOddsRender';
import {
    BB_BETCARD_COLOR_TEXT_BLUE_CSS_CLASS_NAME_MODIFIER,
} from '../react/BetCardClassic/constants';
import isUndefined from 'lodash/isUndefined';
import i18n from '../../utils/i18n';
import { getLocalizedDateFormat } from '~shared/utils/date-time';
import { default as Today } from 'date-fns/is_today';
import { DATE_TIME_FORMAT_DD_MMM, DATE_TIME_FORMAT_HM } from '~shared/utils/constants';
import { getTeamLogoUrl } from '~shared/utils/sportsbook';
import { getSilksBackgroundInlineStyle } from '~scenes/sport/horse-racing/utils';

/**
 * Creates a sport selection view
 * @param {String} id The selection id
 * @return {SportSelectionView} The sport selection view
 */
export default function createSportSelectionView({
    id,
    selection_name,
    sport,
    is_inplay,
    sp_only,
    suspended,
    event_id,
    event_name,
    market_id,
    market_name,
    scheduled_start,
    decimal_price,
    fraction_price,
    american_price,
    extra_info,
    counter,
    displaySelectionNameOdds,
    gtmAttribute,
    isOutright,
    tournamentName,
    category,
    team,
    tournamentLogos,
}) {

    /**
     * Maintains the updated state of the sport selection view
     * @type {Object}
     */
    let currentState = { suspended, decimal_price, fraction_price, sp_only };

    /**
     * The sport selection dom element
     * @type {Element}
     */
    const el = create('div', CSS_CLASS_BLOCK_NAME);

    /**
     * The symbol section dom element
     * @type {Element}
     */
    const symbolSection = create('div', SYMBOL_SECTION_CSS_CLASS_BLOCK_NAME);

    /**
     * The details section dom element
     * @type {Element}
     */
    const detailsSection = create('div', DETAILS_SECTION_CSS_CLASS_BLOCK_NAME);

    /**
     * The selection view
     * @type {Selection}
     */
    const selectionView = new Selection({
        initialState: { id, suspended, decimal_price, fraction_price, sp_only},
        gtmAttribute,
    });

    /**
     * The selection status dom element
     * @type {Element}
     */
    const status = create(
        'span',
        `${STATUS_CSS_CLASS_BLOCK_NAME} ${isInplay() ? STATUS_INPLAY_CSS_CLASS_MODIFIER : ''}`,
        isInplay() ? i18n.t(I18N_INPLAY_STATUS_TEXT) : ''
    );

    /**
     * The selection status label dom element
     * @type {Element}
     */
    const statusLabel = create(
        'span',
        STATUS_LABEL_CSS_CLASS_BLOCK_NAME,
    );

    /**
     * The selection name dom element
     * @type {Element}
     */
    const selectionName = create(
        'span',
        SELECTION_NAME_CSS_CLASS_BLOCK_NAME,
        create('span', TEXT_CSS_CLASS_BLOCK_NAME, selection_name)
    );

    /**
     * The display of betting odds element
     */
    const oddsEl = create('span', BB_BETCARD_COLOR_TEXT_BLUE_CSS_CLASS_NAME_MODIFIER, '');

    /**
     * The market name dom element
     * @type {Element}
     */
    const marketName = create('span', MARKET_NAME_CSS_CLASS_BLOCK_NAME, market_name);

    /**
     * The event name dom element
     * @type {Element}
     */
    const eventName = create('span', EVENT_NAME_CSS_CLASS_BLOCK_NAME, isOutright ? tournamentName : event_name );

    /**
     * The extra info container dom element
     * @type {Element}
     */
    const extraInfoContainer = create('div', EXTRA_INFO_CSS_CLASS_BLOCK_NAME);

    /**
     * The list of unsubscribe functionsfrom subscriptions
     * @type {Set}
     */
    const unsubscriptions = new Set();

    /**
     * React callbacks to run on unmount
     */
    const reactUnmountCallbacks = [];

    /**
     * Returns the selection number of bets
     * @return {int} The selection number of bets
     */
    function getNumberOfBets() {
        return counter;
    }

    /**
     * Returns the selection id
     * @return {String} The selection id
     */
    function getId() {
        return id;
    }

    /**
     * Returns the selection event id
     * @return {String} The selection event id
     */
    function getEventId() {
        return event_id;
    }

    /**
     * Returns the selection market id
     * @return {String} The selection market id
     */
    function getMarketId() {
        return market_id;
    }

    /**
     * Connect to websocket for updates
     * @return {void}
     */
    function connect() {
        if (isInplay()) {
            startListeningForInplayEventUpdates(
                getEventUpdateSubscriptionData()
            );

            unsubscriptions.add(
                onInplaySelectionUpdate(
                    getUpdateSubscriptionData(),
                    update
                )
            );

            unsubscriptions.add(
                onInplayEventOverById(getEventId(), () => {
                    unconnect();
                    selectionView.suspend();
                })
            );

        } else {
            startListeningForPreplayEventUpdates(
                getUpdateSubscriptionData_v2()
            );

            unsubscriptions.add(
                onPreplaySelectionUpdate(
                    getUpdateSubscriptionData(),
                    update
                )
            );

            unsubscriptions.add(
                onPreplayEventStartById(getEventId(), () => {
                    unconnect();
                    selectionView.suspend();
                })
            );

            unsubscriptions.add(
                onPreplayEventOffById(getEventId(), () => {
                    unconnect();
                    selectionView.suspend();
                })
            );
        }
    }

    /**
     * Unconnect from websocket for updates
     * @return {void}
     */
    function unconnect() {
        if (!isInplay()) {
            stopListeningForPreplayEventUpdates(
                getUpdateSubscriptionData_v2()
            );
        } else {
            unsubscriptions.forEach((unsubscribe) => {
                unsubscribe();
                unsubscriptions.delete(unsubscribe);
            });
        }
    }

    /**
     * Checks if the selection is inplay
     * @return {Boolean} true if the event is inplay
     */
    function isInplay() {
        return is_inplay;
    }

    /**
     * Adds trainer info
     * @return {void}
     */
    function addTrainerInfo() {
        const trainerElement = create('span', TRAINER_CSS_CLASS_BLOCK_NAME);

        trainerElement.appendChild(
            create('span', DETAILS_LABEL_CSS_CLASS_BLOCK_NAME, `${i18n.t(I18N_TRAINER_LABEL_TEXT)}: `)
        );

        trainerElement.appendChild(
            create('span', DETAILS_VALUE_CSS_CLASS_BLOCK_NAME, extra_info.trainer_name)
        );

        extraInfoContainer.appendChild(trainerElement);
    }

    /**
     * Adds jockey info
     * @return {void}
     */
    function addJockeyInfo() {
        const jockeyElement = create('span', JOCKEY_CSS_CLASS_BLOCK_NAME);

        jockeyElement.appendChild(
            create('span', DETAILS_LABEL_CSS_CLASS_BLOCK_NAME, `${i18n.t(I18N_JOCKEY_LABEL_TEXT)}: `)
        );

        jockeyElement.appendChild(
            create('span', DETAILS_VALUE_CSS_CLASS_BLOCK_NAME, extra_info.jockey_name)
        );

        extraInfoContainer.appendChild(jockeyElement);
    }

    /**
     * Updates the selection sport view with the provided changeable states
     * @param {Object} newState The new state, must contain {sp_only, decimal_price, fraction_price}
     * @return {void}
     */
    function update(newState = {}) {
        currentState = {...currentState, ...newState};
        selectionView.update(currentState);
        if (!isUndefined(displaySelectionNameOdds) && displaySelectionNameOdds) {
            nextRepaint(() => setSelectionNameOdds(currentState));
        }
    }

    /**
     * Returns the data to listen for selection updates (for inplay)
     * @return {Object} The selection subscription data
     */
    function getUpdateSubscriptionData() {
        return {
            selectionId: getId(),
            marketId: getMarketId(),
            eventId: getEventId(),
        };
    }

    /**
     * Returns the data to listen for selection updates (for v2 subscribe/unsubscribe requests)
     * @see wsInterface.subscribe()
     * @returns {Array} The selection subscription data
     */
    function getUpdateSubscriptionData_v2() {
        return [{
            event_id: getEventId(),
            market_id: getMarketId(),
            selection_id: getId(),
        }];
    }

    /**
     * Returns the data to register for event updates (for legacy)
     * @return {Array} The event subscription data
     */
    function getEventUpdateSubscriptionData() {
        return [{
            event_id: getEventId(),
            markets: [getMarketId()],
        }];
    }

    /**
     * Render the selection odds into the odds element
     * @param {Object} selectionInfo the selection info to display
     * @returns {Void} Nothing
     */
    function setSelectionNameOdds(selectionInfo) {
        const unmountCallback = renderSwitchableOdds({
            selection: selectionInfo,
        }, oddsEl);

        reactUnmountCallbacks.push(unmountCallback);
    }

    /**
     * The sport icon
     */
    if (sport === HORSE_RACING_SLUG && extra_info?.jockey_filename) {
        const silkIcon = create('div', `${SYMBOL_SECTION_CSS_CLASS_BLOCK_NAME}--silk ${CLASS_BLOCK_NAME}--${SIZE_24x24} `);
        silkIcon.setAttribute('style', `background-image: ${getSilksBackgroundInlineStyle(extra_info.jockey_filename)}`);
        symbolSection.appendChild(silkIcon);
    } else if (tournamentLogos?.showTeamLogo && category && team) {
        const teamIcon = create('img', `${CLASS_BLOCK_NAME}--${SIZE_24x24}`);
        teamIcon.setAttribute('src', getTeamLogoUrl(sport, category, team));
        symbolSection.appendChild(teamIcon);
    } else {
        const icon = new Icon(sport, SIZE_24x24);
        symbolSection.appendChild(icon.el);
    }

    symbolSection.appendChild(status);

    if (!isInplay()) {
        symbolSection.appendChild(statusLabel);
    }

    el.appendChild(symbolSection);

    if (!isUndefined(displaySelectionNameOdds) && displaySelectionNameOdds) {
        const selection = {
            sp_only,
            decimal_price,
            fraction_price,
            american_price,
        };
        setSelectionNameOdds(selection);
        selectionName.appendChild(oddsEl);
    }

    detailsSection.appendChild(selectionName);
    detailsSection.appendChild(marketName);
    detailsSection.appendChild(eventName);
    detailsSection.appendChild(extraInfoContainer);

    el.appendChild(detailsSection);

    selectionView.el.classList.add(SELECTION_CSS_CLASS_BLOCK_NAME);

    if (sport === HORSE_RACING_SLUG && extra_info && extra_info.jockey_name) {
        addJockeyInfo();
    }

    if ((sport === HORSE_RACING_SLUG || sport === GREYHOUND_RACING_SLUG) && extra_info && extra_info.trainer_name) {
        addTrainerInfo();
    }

    if (!isInplay()) {
        renderDateEl({
            dateTime: scheduled_start,
            format: getLocalizedDateFormat(DATE_TIME_FORMAT_HM),
        }, status);
        if (!Today(scheduled_start)) {
            renderDateEl({
            dateTime: scheduled_start,
            format: getLocalizedDateFormat(DATE_TIME_FORMAT_DD_MMM),
            }, statusLabel);
        }
    }

    return {
        el,
        getId,
        update,
        connect,
        isInplay,
        unconnect,
        getEventId,
        getMarketId,
        getNumberOfBets,
        selectionView,
        getUpdateSubscriptionData,
        getEventUpdateSubscriptionData,
        getUpdateSubscriptionData_v2,
        reactUnmountCallbacks,
    };
}
